import { CountryCodes, type CountryRecord } from '@/enums/general'
import {
  BoysPrices,
  MenPrices,
  NewOrientations,
  SocksDiscountPrices,
  SocksPrices,
  UndershirtsDiscountPrices,
  UndershirtsPrices,
  WomenPrices
} from '@/enums/products'
import type { MemberStatus, MembershipTypeReference } from '@/types/member'

type CountryPrices = CountryRecord<number>
type DiscountedCountryPrices = CountryPrices & { discount: CountryPrices }

export const EasyPauseActivateModes = {
  activate: 'Activate',
  pause: 'Pause'
} as const

export const MemberStatuses = {
  disabled: 0,
  enabled: 1,
  paused: 2,
  new: 3,
  waste: 66
} as const

export const NewMemberStatusesKeys = {
  inactive: 'inactive',
  active: 'active',
  paused: 'paused',
  trial_period: 'trial_period',
  waste: 'waste'
} as const

export const NotActiveMembershipStatuses: MemberStatus[] = [
  NewMemberStatusesKeys.inactive,
  NewMemberStatusesKeys.paused
]

export const NewMemberStatuses = {
  [NewMemberStatusesKeys.inactive]: 0,
  [NewMemberStatusesKeys.active]: 1,
  [NewMemberStatusesKeys.paused]: 2,
  [NewMemberStatusesKeys.trial_period]: 3,
  [NewMemberStatusesKeys.waste]: 66
} as const

export const ManageSubscriptionReasons = {
  freeTest: 'FreeTest',
  tooMany: 'TooMany',
  financial: 'Financial',
  quality: 'Quality',
  prints: 'Prints',
  boring: 'Boring',
  break: 'Break',
  delayedDelivery: 'Delayed Delivery',
  other: 'Other'
} as const

export const MemberCampaigns = {
  reactivation: 1,
  promotion: 2,
  pleaseStay: 3,
  pleaseDontLeave: 8
} as const

export const NewMemberCampaigns = {
  pleaseStay: 'please_stay'
} as const

export const stepForm = {
  reasonList: 'reasonList',
  inactive: 'inactive',
  paused: 'paused',
  testFree: 'testFree',
  tryToPause: 'tryToPause',
  tryReturnOrder: 'tryReturnOrder',
  tryOriginals: 'tryOriginals',
  tryOne: 'tryOne',
  otherReason: 'otherReason',
  cancelMembership: 'cancelMembership',
  pleaseStay: 'pleaseStay',
  loyaltyPleaseStay: 'loyaltyPleaseStay',
  confirmation: 'confirmation',
  reactivated: 'reactivated',
  socksPleaseStay: 'socksPleaseStay'
} as const

export const MaxCancelDates: CountryRecord<number> = {
  [CountryCodes.austria]: 20,
  [CountryCodes.belgium]: 20,
  [CountryCodes.denmark]: 20,
  [CountryCodes.germany]: 20,
  [CountryCodes.spain]: 20,
  [CountryCodes.france]: 20,
  [CountryCodes.netherlands]: 20,
  [CountryCodes.unitedKingdom]: 20,
  [CountryCodes.sweden]: 20,
  [CountryCodes.italy]: 20,
  [CountryCodes.poland]: 20,
  [CountryCodes.switzerland]: 20,
  [CountryCodes.ireland]: 20,
  [CountryCodes.luxembourg]: 20,
  [CountryCodes.portugal]: 20,
  [CountryCodes.finland]: 20,
  [CountryCodes.norway]: 20,
  [CountryCodes.czechRepublic]: 20
}

export const TrialDuration: CountryRecord<number> = {
  [CountryCodes.austria]: 14,
  [CountryCodes.belgium]: 14,
  [CountryCodes.denmark]: 14,
  [CountryCodes.germany]: 14,
  [CountryCodes.spain]: 14,
  [CountryCodes.france]: 14,
  [CountryCodes.netherlands]: 14,
  [CountryCodes.unitedKingdom]: 14,
  [CountryCodes.sweden]: 14,
  [CountryCodes.italy]: 14,
  [CountryCodes.poland]: 14,
  [CountryCodes.switzerland]: 14,
  [CountryCodes.ireland]: 14,
  [CountryCodes.luxembourg]: 14,
  [CountryCodes.portugal]: 14,
  [CountryCodes.finland]: 14,
  [CountryCodes.norway]: 14,
  [CountryCodes.czechRepublic]: 14
}

export const MembershipTypes = {
  women: 'women-membership',
  sneakerSocks: 'originals-socks-sneaker-membership',
  socksRegular: 'originals-socks-regular-membership',
  boysOriginals: 'boys-originals-membership',
  menOriginals: 'men-originals-membership',
  menOne: 'men-one-membership',
  undershirts: 'men-undershirts-membership'
} as const

export const MembershipPrice: Record<
  MembershipTypeReference,
  CountryPrices | DiscountedCountryPrices
> = {
  [MembershipTypes.menOriginals]: MenPrices,
  [MembershipTypes.menOne]: MenPrices,
  [MembershipTypes.sneakerSocks]: { ...SocksPrices, discount: SocksDiscountPrices },
  [MembershipTypes.socksRegular]: { ...SocksPrices, discount: SocksDiscountPrices },
  [MembershipTypes.boysOriginals]: BoysPrices,
  [MembershipTypes.women]: WomenPrices,
  [MembershipTypes.undershirts]: { ...UndershirtsPrices, discount: UndershirtsDiscountPrices }
}

export const CancelMembershipFormSteps = {
  trialMemberWithinWaitingDays: 'trialMemberWithinWaitingDays',
  allReasonsList: 'allReasonsList',
  initialLoading: 'initialLoading',
  hasOpenPayment: 'hasOpenPayment',
  pauseFlow: 'pauseFlow',
  inactiveMember: 'inactiveMember',
  isActiveToStopMember: 'isActiveToStopMember',
  isPausedMember: 'isPausedMember',
  switchToMenOne: 'switchToMenOne',
  switchToMenOriginals: 'switchToMenOriginals',
  getDiscount: 'getDiscount',
  getDiscountVariantA: 'getDiscountVariantA',
  lastStep: 'lastStep',
  monthlyOrderIsNotDeliveredYet: 'monthlyOrderIsNotDeliveredYet',
  monthlyOrderShouldBeenDelivered: 'monthlyOrderShouldBeenDelivered',
  monthlyOrderShouldBeenDeliveredAddressCheck: 'monthlyOrderShouldBeenDeliveredAddressCheck',
  receivedWrongSize: 'receivedWrongSize',
  receivedWrongDesignDamagedProduct: 'receivedWrongDesignDamagedProduct',
  cantDoReturnRequest: 'cantDoReturnRequest',
  waitTillNextWomenSet: 'waitTillNextWomenSet',
  tryAnotherMembershipType: 'tryAnotherMembershipType',
  tryAnotherMembershipTypeSelectSize: 'tryAnotherMembershipTypeSelectSize',
  pickAvailableProductToReturn: 'pickAvailableProductToReturn',
  applyForRecurrence: 'applyForRecurrence'
} as const

export const CancelReasons = {
  IWantToTestForFree: 'IWantToTestForFree',
  IHaveEnoughProducts: 'IHaveEnoughProducts',
  IHaveDeliveryProblems: 'IHaveDeliveryProblems',
  IDontWantAMemberShip: 'IDontWantAMemberShip',
  IDontLikeTheQuality: 'IDontLikeTheQuality',
  IHaveEnoughMenOriginals: 'IHaveEnoughMenOriginals',
  IHaveEnoughMenOne: 'IHaveEnoughMenOne',
  IDontLikeTheDesigns: 'IDontLikeTheDesigns'
} as const

export const DeliveryIssueReasons = {
  DidNotReceiveMyOrder: 'DidNotReceiveMyOrder',
  ReceivedWrongSize: 'ReceivedWrongSize',
  ReceivedWrongDesign: 'ReceivedWrongDesign',
  ReceivedADamagedProduct: 'ReceivedADamagedProduct',
  SizeIsTooSmall: 'SizeIsTooSmall',
  SizeIsTooBig: 'SizeIsTooBig'
} as const

export const GTMActions = {
  waitTillNextWomenSet: 'waitTillNextWomenSet',
  cancelReasonConfirmed: 'cancelReasonConfirmed',
  exit: 'exit',
  changeMembershipSelected: 'changeMembershipSelected',
  changeMembershipConfirmed: 'changeMembershipConfirmed',
  continue: 'continue',
  deliveryIssueReasonConfirmed: 'deliveryIssueReasonConfirmed',
  returnRequestConfirmed: 'returnRequestConfirmed',
  pauseConfirmed: 'pauseConfirmed',
  remainMemberConfirmed: 'remainMemberConfirmed',
  cancelMembershipConfirmed: 'cancelMembershipConfirmed',
  testForFreeConfirmed: 'testForFreeConfirmed',
  trySimilarDesignConfirmed: 'trySimilarDesignConfirmed',
  pleaseStayConfirmed: 'pleaseStayConfirmed',
  waitForOrderDeliveryConfirmed: 'waitForOrderDeliveryConfirmed',
  applyForRecurrenceConfirmed: 'applyForRecurrenceConfirmed'
} as const

export const RecurrenceOptions = {
  everyMonth: 'everyMonth',
  everyOtherMonth: 'everyOtherMonth'
} as const

export const MainMembershipTypes = [
  MembershipTypes.menOriginals,
  MembershipTypes.menOne,
  MembershipTypes.boysOriginals,
  MembershipTypes.women
] as const

export const MembershipTypesToNewOrientations = {
  [MembershipTypes.women]: NewOrientations.women,
  [MembershipTypes.sneakerSocks]: NewOrientations.addon,
  [MembershipTypes.socksRegular]: NewOrientations.addon,
  [MembershipTypes.boysOriginals]: NewOrientations.boys,
  [MembershipTypes.menOriginals]: NewOrientations.men,
  [MembershipTypes.menOne]: NewOrientations.men,
  [MembershipTypes.undershirts]: NewOrientations.addon
} as const
